/* sugar:assets/lazy-images.js@2.0.0 */
/*
 * Preload lazy images slightly before they enter the viewport.
 * Native loading="lazy" thresholds vary across browsers and can feel late
 * when script payload delays evaluation. This observer bumps images to eager
 * ~1500px before they become visible, then stops watching them.
 *
 * Icons below the first screen (snippets/icon.liquid marks them
 * data-icon-lazy="<file>") wait here too; see the icon section further down.
 */
(function () {
  if (!('IntersectionObserver' in window)) {
    // Nothing can wait for the screen without an observer: show every icon now.
    var all = document.createElement('style');
    all.textContent = '.sugar-icon[data-icon-lazy]{-webkit-mask-image:var(--icon-url);mask-image:var(--icon-url)}';
    document.head.appendChild(all);
    return;
  }

  // How far below the viewport the browser starts a lazy image on its own
  // (measured 2026-09-25): Chrome 1250px, or 2500px on a 3G-or-slower
  // connection; WebKit exactly one viewport height (Safari, and every iOS
  // browser, which all run on WebKit). Anything else gets Chrome's 4G figure.
  var ua = navigator.userAgent;
  var webkit = /AppleWebKit/.test(ua) && !/Chrome|Chromium|Edg/.test(ua);
  function nativeThreshold() {
    if (webkit) return window.innerHeight || document.documentElement.clientHeight;
    var c = navigator.connection;
    if (c && c.effectiveType && c.effectiveType !== '4g') return 2500;
    return 1250;
  }

  // A responsive image declares its width in viewport terms because the
  // template cannot know the column it lands in; a grid cell at a third of the
  // page would fetch a full-width file. So the width it actually got is pinned
  // as its sizes, once it is laid out and before the browser picks a candidate
  // on its own. An image already inside the browser's own threshold (on the
  // page at load, or just revealed by a slider or gallery) has a candidate in
  // flight; re-pinning it would start a second download of the same image, so
  // it keeps the candidate its sizes picked.
  // The rect an observer reports is a frame old by the time its callback runs,
  // so during a fast scroll an image can cross the browser's own line in
  // between: only images still a margin beyond it are pinned.
  var SAFETY = 300;
  var pinMargin = nativeThreshold() + SAFETY + 600;
  var pinner = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;
        var img = entry.target;
        pinner.unobserve(img);
        if (!img.hasAttribute('srcset') || !img.hasAttribute('sizes')) continue;
        var rect = entry.boundingClientRect;
        var viewport = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top - viewport <= nativeThreshold() + SAFETY) continue;
        if (rect.width > 0) img.sizes = Math.ceil(rect.width) + 'px';
      }
    },
    { rootMargin: '0px 0px ' + pinMargin + 'px 0px' }
  );

  var io = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        var img = entries[i].target;
        img.loading = 'eager';
        io.unobserve(img);
        // A <picture> picks its file by screen width: eager loads that one, and a copy built
        // from the <img> alone would also fetch the other screen's file.
        if (img.complete || img.parentElement.localName === 'picture') continue;
        var srcset = img.getAttribute('srcset');
        var src = img.getAttribute('src');
        if (!srcset && !src) continue;
        var preload = new Image();
        if (srcset) {
          preload.sizes = img.getAttribute('sizes') || '';
          preload.srcset = srcset;
        }
        if (src) preload.src = src;
      }
    },
    { rootMargin: '0px 0px 1500px 0px' }
  );

  function watch(img) {
    pinner.observe(img);
    io.observe(img);
  }

  // Icons below the first screen paint nothing (base.css) until their file is
  // here. One already on screen before the page has loaded (a short page, a
  // sticky bar) is fetched at once; the rest wait for the load event, so they
  // never share the connection with the first screen's pictures, fonts and
  // icons, and then arrive together, ready for whatever scrolls or opens next.
  // Each file is fetched once, through a preload the icon's own mask reuses
  // (WebKit downloads an <img> or new Image() a second time for a mask), and
  // one rule then shows every icon of that file: those on the page and any a
  // slider, marquee or template adds later. A file that fails hides its icons,
  // as a missing token does on the first screen.
  var iconSheet = null;
  var iconFiles = {};
  var iconLoaded = document.readyState === 'complete';
  function iconRule(css) {
    if (!iconSheet) {
      var style = document.createElement('style');
      document.head.appendChild(style);
      iconSheet = style.sheet;
    }
    try {
      iconSheet.insertRule(css, iconSheet.cssRules.length);
    } catch (e) {}
  }
  function iconFetch(file, url, shown) {
    iconFiles[file] = true;
    var sel = '.sugar-icon[data-icon-lazy="' + file.replace(/["\\]/g, '\\$&') + '"]';
    // An icon inside a closed tab, row or drawer cannot use a preload yet, and an
    // unused preload draws a console warning; an image warms the cache instead.
    var loader = shown ? document.createElement('link') : new Image();
    loader.crossOrigin = 'anonymous';
    loader.onload = function () {
      // RECOV CORE EDIT 2026-10-21 (round 9 candidate): the loaded file's URL as a literal (was var(--icon-url));
      // same selector and specificity, so the cascade is unchanged. See snippets/icon.liquid.
      var lit = 'url("' + String(url).replace(/["\\]/g, '\\$&') + '")';
      iconRule(sel + '{-webkit-mask-image:' + lit + ';mask-image:' + lit + '}');
    };
    loader.onerror = function () {
      iconRule(sel + '{display:none!important}');
    };
    if (shown) {
      loader.rel = 'preload';
      loader.as = 'image';
      loader.href = url;
      document.head.appendChild(loader);
    } else {
      loader.src = url;
    }
  }
  function iconWant(want, el, live) {
    var file = el.getAttribute('data-icon-lazy');
    if (!file || iconFiles[file]) return;
    var w = want[file];
    if (!w) {
      var m = /--icon-url:\s*url\(\s*(['"]?)(.*?)\1\s*\)/.exec(el.getAttribute('style') || '');
      w = want[file] = { url: m ? m[2] : '', shown: false };
    }
    if (live && !w.shown && el.getClientRects().length) w.shown = true;
  }
  function iconStart(want) {
    for (var file in want) {
      if (want[file].url) iconFetch(file, want[file].url, want[file].shown);
    }
  }
  // Reads every icon first, then starts the fetches.
  function iconSweep() {
    var want = {};
    var els = document.querySelectorAll('.sugar-icon[data-icon-lazy]');
    for (var i = 0; i < els.length; i++) iconWant(want, els[i], true);
    var tpls = document.querySelectorAll('template');
    for (var t = 0; t < tpls.length; t++) {
      var inner = tpls[t].content.querySelectorAll('.sugar-icon[data-icon-lazy]');
      for (var k = 0; k < inner.length; k++) iconWant(want, inner[k], false);
    }
    iconStart(want);
  }
  var iconIO = new IntersectionObserver(function (entries) {
    var want = {};
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      iconIO.unobserve(entries[i].target);
      iconWant(want, entries[i].target, true);
    }
    iconStart(want);
  });
  function iconWatch(el) {
    if (iconFiles[el.getAttribute('data-icon-lazy')]) return;
    if (!iconLoaded) {
      iconIO.observe(el);
      return;
    }
    var want = {};
    iconWant(want, el, true);
    iconStart(want);
  }
  // A first-screen icon inside something closed (the cart drawer, a video's
  // controls) has a preload its mask has not used, and WebKit warns about each
  // one three seconds after load. An image takes it over without a second
  // download.
  function iconClaimPreloads() {
    var links = document.querySelectorAll('.sugar-icon > link[rel="preload"], .svg-icon--custom > link[rel="preload"]');
    var idle = {};
    for (var i = 0; i < links.length; i++) {
      var icon = links[i].parentNode;
      if (icon.style.display !== 'none' && !icon.getClientRects().length) idle[links[i].href] = true;
    }
    for (var url in idle) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
    }
  }
  function iconAfterLoad() {
    iconLoaded = true;
    iconIO.disconnect();
    iconClaimPreloads();
    var later = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };
    later(iconSweep, { timeout: 2000 });
  }
  if (iconLoaded) iconAfterLoad();
  else window.addEventListener('load', iconAfterLoad);

  function observe(root) {
    var found = (root || document).querySelectorAll('img[loading="lazy" i], .sugar-icon[data-icon-lazy]');
    for (var j = 0; j < found.length; j++) {
      if (found[j].tagName === 'IMG') watch(found[j]);
      else iconWatch(found[j]);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(); });
  } else {
    observe();
  }
  new MutationObserver(function (mutations) {
    for (var m = 0; m < mutations.length; m++) {
      for (var n = 0; n < mutations[m].addedNodes.length; n++) {
        var node = mutations[m].addedNodes[n];
        if (node.nodeType !== 1) continue;
        if (node.tagName === 'IMG' && node.loading === 'lazy') watch(node);
        else if (node.hasAttribute('data-icon-lazy')) iconWatch(node);
        else observe(node);
      }
    }
  }).observe(document.body || document.documentElement, { childList: true, subtree: true });
})();
